import { Box, Flex, UseDisclosureReturn } from "@chakra-ui/react"
import { AnimatePresence, motion } from "framer-motion"
import React, { createContext, useContext, useMemo, useRef } from "react"
import { usePreviousDistinct } from "react-use"
import {
  SharedSizeProvider,
  useSharedSize,
  useSharedSizeRef
} from "../../utils/shared-size"
import {
  AugmentationOffsetOptions,
  ComputeAugmentationOffsetResult,
  computeAugmentationOffset
} from "./useAugmentationOffset"
import { UseAugmentationPanelProps, usePanelSize } from "./useAugmentationPanel"

export type AugmentationRole = "layout" | "primary" | "panel" | "toolbar"

export type AugmentationCoordinatorConfig = {
  disclosure: UseDisclosureReturn
  spacing?: number | [number, number] | [number, number, number]
  allowOverlay?: boolean
  panelPosition?: "near" | "far"
  offset?: AugmentationOffsetOptions
  panelSize?: UseAugmentationPanelProps["size"]
  /**
   * Explicit alignment. Left panel support is incomplete — prefer `"right"`.
   * NOTE: aligning panel to the left does not currently work. This requires
   * updating the useAugmentationPanel hook to support left alignment.
   * Also, toolbar doesn't respect the alignment either.
   */
  align?: "left" | "right"
  hasPrimary?: boolean
  hasToolbar?: boolean
}

type AugmentationCoordinatorContextValue = {
  config: Required<
    Pick<
      AugmentationCoordinatorConfig,
      "panelPosition" | "align" | "allowOverlay" | "hasPrimary" | "hasToolbar"
    >
  > &
    AugmentationCoordinatorConfig
  layoutSpacing: number
  result: ComputeAugmentationOffsetResult
  appliedPanelOffset: number
  panelTokenSize: ReturnType<typeof usePanelSize>
}

const AugmentationCoordinatorContext =
  createContext<AugmentationCoordinatorContextValue | null>(null)

export function useAugmentationCoordinator() {
  const context = useContext(AugmentationCoordinatorContext)
  if (!context) {
    throw new Error(
      "useAugmentationCoordinator must be used within an AugmentationCoordinatorProvider"
    )
  }
  return context
}

function resolveSpacings(
  spacing: AugmentationCoordinatorConfig["spacing"],
  disclosureOpen: boolean,
  hasToolbar: boolean
) {
  // Default the spacing when not provided.
  // TODO: work out how to adjust spacing based on number of components,
  // order of components, and number of spacings.
  const spacings = ([] as number[]).concat(spacing || 10)
  const baseSpacing = spacings[0]
  const primaryAndPanelSpacing = spacings[0] || baseSpacing
  const panelAndToolbarSpacing =
    disclosureOpen && hasToolbar ? spacings[1] || baseSpacing : 0
  const layoutSpacing = spacings[2] ?? spacings[1] ?? baseSpacing
  return { primaryAndPanelSpacing, panelAndToolbarSpacing, layoutSpacing }
}

function AugmentationCoordinatorInner({
  children,
  disclosure,
  spacing,
  allowOverlay = false,
  panelPosition = "far",
  offset,
  panelSize,
  align = "right",
  hasPrimary = true,
  hasToolbar = true
}: AugmentationCoordinatorConfig & { children: React.ReactNode }) {
  const layoutSize = useSharedSize("layout")
  const primarySize = useSharedSize("primary")
  const panelSizeRect = useSharedSize("panel")
  const toolbarSize = useSharedSize("toolbar")

  const layoutWidth = layoutSize?.width || 0
  const primaryWidth = hasPrimary ? primarySize?.width || 0 : 0
  let panelWidth = panelSizeRect?.width || 0
  const toolbarWidth = hasToolbar ? toolbarSize?.width || 0 : 0

  // We need to preserve the previous panel size to fall back to since
  // otherwise reopening the panel can result in animating the panel
  // initially from an incorrect position.
  const prevPanelWidth = usePreviousDistinct(panelWidth)
  // Widths of the elements.
  panelWidth ||= prevPanelWidth || 0

  const panelTokenSize = usePanelSize({
    allowOverlay,
    size: panelSize
  })

  // Seems to be a bug in useMeasure that retains the size of the element even after it
  // has unmounted. So, we need to manually track whether the panel is open, and, if not,
  // reset panel width to 0.
  if (!disclosure.open) {
    panelWidth = 0
  }

  // Ignoring offset when panel is full size.
  if (panelTokenSize === "full") {
    panelWidth = 0
  }

  const { primaryAndPanelSpacing, panelAndToolbarSpacing, layoutSpacing } =
    resolveSpacings(spacing, disclosure.open, hasToolbar)

  const result = useMemo(
    () =>
      computeAugmentationOffset({
        offset,
        panelPosition,
        alignAugmentation: align,
        panelSize: String(panelTokenSize || ""),
        hasPrimary,
        hasToolbar,
        layoutWidth,
        primaryWidth,
        panelWidth,
        toolbarWidth,
        primaryAndPanelSpacing,
        panelAndToolbarSpacing,
        layoutSpacing
      }),
    [
      offset,
      panelPosition,
      align,
      panelTokenSize,
      hasPrimary,
      hasToolbar,
      layoutWidth,
      primaryWidth,
      panelWidth,
      toolbarWidth,
      primaryAndPanelSpacing,
      panelAndToolbarSpacing,
      layoutSpacing
    ]
  )

  // Keep the last open-state panel offset while closing. Otherwise zeroing
  // panelWidth and remounting the toolbar (e.g. CertificateAugmentation)
  // recalculates offset mid-exit and jerks the drawer horizontally.
  const openPanelOffsetRef = useRef(result.panelOffset)
  if (disclosure.open) {
    openPanelOffsetRef.current = result.panelOffset
  }
  const appliedPanelOffset = disclosure.open
    ? result.panelOffset
    : openPanelOffsetRef.current

  const config = useMemo(
    () => ({
      disclosure,
      spacing,
      allowOverlay,
      panelPosition,
      offset,
      panelSize,
      align,
      hasPrimary,
      hasToolbar
    }),
    [
      disclosure,
      spacing,
      allowOverlay,
      panelPosition,
      offset,
      panelSize,
      align,
      hasPrimary,
      hasToolbar
    ]
  )

  const value = useMemo(
    () => ({
      config,
      layoutSpacing,
      result,
      appliedPanelOffset,
      panelTokenSize
    }),
    [config, layoutSpacing, result, appliedPanelOffset, panelTokenSize]
  )

  return (
    <AugmentationCoordinatorContext.Provider value={value}>
      {children}
    </AugmentationCoordinatorContext.Provider>
  )
}

/**
 * Scopes shared sizes + offset policy for Primary / Panel / Toolbar.
 * Components may mount anywhere under this provider and register via
 * `useAugmentationMeasure`.
 */
export function AugmentationCoordinatorProvider({
  children,
  ...config
}: AugmentationCoordinatorConfig & { children: React.ReactNode }) {
  return (
    <SharedSizeProvider>
      <AugmentationCoordinatorInner {...config}>
        {children}
      </AugmentationCoordinatorInner>
    </SharedSizeProvider>
  )
}

AugmentationCoordinatorProvider.displayName = "AugmentationCoordinatorProvider"

/** Attach a resize observer that publishes this role's size to the coordinator. */
export function useAugmentationMeasure(
  role: AugmentationRole
): React.RefCallback<HTMLElement> {
  useAugmentationCoordinator()
  return useSharedSizeRef(role)
}

export function useAugmentationPrimaryStyles() {
  const { result } = useAugmentationCoordinator()
  return {
    primaryHorizontalAlignment: result.primaryHorizontalAlignment,
    primaryOffsetProp: result.primaryOffsetProp,
    primaryMotionProps: result.primaryMotionProps,
    primaryLayout: result.primaryLayout,
    primarySizeProps: result.primarySizeProps
  }
}

/**
 * Reserved space on the right side of the screen
 * for fixed/portaled content isn't controlled by the Augmentation layout
 */
export function useAugmentationContentInset() {
  const { result } = useAugmentationCoordinator()
  return result.contentInset
}

export function useAugmentationPanelOffset(): {
  left?: number
  right?: number
} {
  const { config, appliedPanelOffset } = useAugmentationCoordinator()
  return { [config.align]: appliedPanelOffset }
}

export function useAugmentationToolbarOffset(): {
  left?: number
  right?: number
} {
  const { config, result } = useAugmentationCoordinator()
  return { [config.align]: result.toolbarOffset }
}

export function useAugmentationLayoutSpacing() {
  return useAugmentationCoordinator().layoutSpacing
}

type AugmentationLayoutFrameProps = {
  children: React.ReactNode
  className?: string
}

/** Outer layout shell: measures layout width and applies primary flex alignment. */
export function AugmentationLayoutFrame({
  children,
  className = "AugmentationLayout"
}: AugmentationLayoutFrameProps) {
  const layoutRef = useAugmentationMeasure("layout")
  const { result } = useAugmentationCoordinator()

  // NOTE: the "sizer" elements are used to determine the width of their parent.
  // They explicitly are rendered at the top of the parent element with 0 height
  // so that the resize observer doesn't concern itself with the height of the
  // parent element.
  return (
    <Box className={className} w="100%" h="100%">
      {/* Layout width sizer */}
      <Box ref={layoutRef} h={0} w="100%" />
      <Flex
        className="AugmentationLayoutContent"
        justify={result.primaryHorizontalAlignment}
        align="flex-start"
        maxW="100%"
        h="100%"
        pos="relative"
      >
        {children}
      </Flex>
    </Box>
  )
}

type AugmentationPrimarySurfaceProps = {
  children: React.ReactNode
  className?: string
}

/** Primary surface that measures itself and applies offset / constrain styles. */
export function AugmentationPrimarySurface({
  children,
  className = "AugmentationLayoutPrimary"
}: AugmentationPrimarySurfaceProps) {
  const primaryRef = useAugmentationMeasure("primary")
  const {
    primaryOffsetProp,
    primaryMotionProps,
    primaryLayout,
    primarySizeProps
  } = useAugmentationPrimaryStyles()

  return (
    <AnimatePresence initial={false}>
      <Box
        className={className}
        asChild
        h="100%"
        pos="relative"
        {...primarySizeProps}
        {...primaryOffsetProp}
      >
        <motion.div layout={primaryLayout} {...primaryMotionProps}>
          {/* Primary width sizer */}
          <Box ref={primaryRef} h={0} w="100%" />
          {children}
        </motion.div>
      </Box>
    </AnimatePresence>
  )
}
